/**
 * Hook personalizado para usar Analytics con React Query
 * Maneja el estado de carga, errores y cache de datos
 */

import {
  useQuery,
  UseQueryResult,
  keepPreviousData,
} from "@tanstack/react-query";
import AnalyticsService, {
  GlobalAnalysisResponse,
  StoreAnalysisResponse,
  UserAnalysisResponse,
  PosAnalysisResponse,
  DashboardLevel1Metrics,
} from "../services/analytics.service";

// ===========================
// GLOBAL ANALYTICS HOOKS
// ===========================

export const useGlobalAnalysis = (): UseQueryResult<GlobalAnalysisResponse> => {
  return useQuery({
    queryKey: ["analytics", "global", "overview"],
    queryFn: AnalyticsService.getGlobalAnalysis,
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchInterval: 10 * 60 * 1000, // Refetch cada 10 minutos
  });
};

export const useGlobalTrends = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "global", "trends"],
    queryFn: AnalyticsService.getGlobalTrends,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCatalogComparison = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "global", "catalog-comparison"],
    queryFn: AnalyticsService.getCatalogComparison,
    staleTime: 15 * 60 * 1000, // 15 minutos - datos menos volátiles
  });
};

// ===========================
// STORE ANALYTICS HOOKS
// ===========================

export const useStoresAnalysis = (): UseQueryResult<StoreAnalysisResponse> => {
  return useQuery({
    queryKey: ["analytics", "stores", "overview"],
    queryFn: AnalyticsService.getStoresAnalysis,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });
};

export const useStoreProducts = (storeName: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "stores", "products", storeName],
    queryFn: () => AnalyticsService.getStoreProducts(storeName),
    enabled: !!storeName,
    staleTime: 5 * 60 * 1000,
  });
};

export const useStoresComparison = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "stores", "comparison"],
    queryFn: AnalyticsService.getStoresComparison,
    staleTime: 10 * 60 * 1000,
  });
};

// ===========================
// POS ANALYTICS HOOKS
// ===========================

export const usePosAnalysis = (): UseQueryResult<PosAnalysisResponse> => {
  return useQuery({
    queryKey: ["analytics", "pos", "overview"],
    queryFn: AnalyticsService.getPosAnalysis,
    staleTime: 3 * 60 * 1000, // 3 minutos - datos más dinámicos
    refetchInterval: 5 * 60 * 1000,
  });
};

export const usePosEfficiency = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "pos", "efficiency"],
    queryFn: AnalyticsService.getPosEfficiency,
    staleTime: 5 * 60 * 1000,
  });
};

export const useOperatorSales = (operatorId: number): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "pos", "operator", operatorId],
    queryFn: () => AnalyticsService.getOperatorSales(operatorId),
    enabled: !!operatorId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useShiftComparison = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "pos", "shifts"],
    queryFn: AnalyticsService.getShiftComparison,
    staleTime: 10 * 60 * 1000,
  });
};

// ===========================
// USER ANALYTICS HOOKS
// ===========================

export const useUsersAnalysis = (): UseQueryResult<UserAnalysisResponse> => {
  return useQuery({
    queryKey: ["analytics", "users", "overview"],
    queryFn: AnalyticsService.getUsersAnalysis,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });
};

export const useUserProductivity = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "users", "productivity"],
    queryFn: AnalyticsService.getUserProductivity,
    staleTime: 10 * 60 * 1000,
  });
};

export const useUserMetrics = (userId: number): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "users", "metrics", userId],
    queryFn: () => AnalyticsService.getUserMetrics(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUserRanking = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "users", "ranking"],
    queryFn: AnalyticsService.getUserRanking,
    staleTime: 10 * 60 * 1000,
  });
};

export const useUserPerformance = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "users", "performance"],
    queryFn: AnalyticsService.getUserPerformance,
    staleTime: 10 * 60 * 1000,
  });
};

export const useUserGrowth = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "users", "growth"],
    queryFn: AnalyticsService.getUserGrowth,
    staleTime: 15 * 60 * 1000,
  });
};

// ===========================
// DASHBOARD HOOKS
// ===========================

export const useDashboardOverview = (): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "dashboard", "overview"],
    queryFn: AnalyticsService.getGlobalAnalysis,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000, // Refetch cada 15 minutos
    retry: 2,
  });
};

/**
 * Hook para Dashboard de Ventas - Métricas mensuales (Odoo 18).
 * Bloque 10 plan dashboard_ux_ventas_margenes 2026-05-21:
 *   Acepta opts opcional { startMonth?, endMonth?, storeName? } — backward-compat.
 *   queryKey incluye los opts para cache distinct por rango/filtros.
 */
export const useDashboardLevel1Metrics = (opts?: {
  startMonth?: string | null;
  endMonth?: string | null;
  storeName?: string | null;
}): UseQueryResult<DashboardLevel1Metrics> => {
  const startMonth = opts?.startMonth ?? null;
  const endMonth = opts?.endMonth ?? null;
  const storeName = opts?.storeName ?? null;
  return useQuery({
    queryKey: [
      "analytics",
      "dashboard",
      "level1",
      "monthly",
      startMonth,
      endMonth,
      storeName,
    ],
    queryFn: () =>
      AnalyticsService.getDashboardMetrics({ startMonth, endMonth, storeName }),
    staleTime: 3 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Alias para DashboardVentas - mismo hook que useDashboardLevel1Metrics
 */
export const useDashboardVentasMetrics = useDashboardLevel1Metrics;

/**
 * NUEVO: Hook para obtener datos específicos de un mes
 */
export const useMonthlyData = (
  year: number,
  month: number,
  enabled: boolean = true,
): UseQueryResult<any> => {
  return useQuery({
    queryKey: ["analytics", "monthly", year, month],
    queryFn: () => AnalyticsService.getMonthlyData(year, month),
    enabled: enabled && !!year && !!month,
    staleTime: 10 * 60 * 1000, // 10 minutos - datos históricos no cambian frecuentemente
    placeholderData: keepPreviousData, // Mantener datos previos mientras carga el nuevo mes
    retry: 2,
  });
};

// ===========================
// UTILITY HOOKS
// ===========================

/**
 * Hook para manejar estado de múltiples queries
 */
export const useAnalyticsStatus = () => {
  const globalQuery = useGlobalAnalysis();
  const storesQuery = useStoresAnalysis();
  const usersQuery = useUsersAnalysis();
  const posQuery = usePosAnalysis();

  const isLoading =
    globalQuery.isLoading ||
    storesQuery.isLoading ||
    usersQuery.isLoading ||
    posQuery.isLoading;

  const isError =
    globalQuery.isError ||
    storesQuery.isError ||
    usersQuery.isError ||
    posQuery.isError;

  const hasData =
    globalQuery.data && storesQuery.data && usersQuery.data && posQuery.data;

  return {
    isLoading,
    isError,
    hasData,
    queries: {
      global: globalQuery,
      stores: storesQuery,
      users: usersQuery,
      pos: posQuery,
    },
  };
};

/**
 * Hook para obtener métricas resumidas del dashboard
 */
export const useDashboardMetrics = () => {
  const dashboardQuery = useDashboardOverview();

  if (!dashboardQuery.data) {
    return null;
  }

  const data = dashboardQuery.data;

  return {
    // Métricas globales principales
    totalProducts: {
      odoo16: data.summary?.totalProducts16 || 0,
      odoo18: data.summary?.totalProducts18 || 0,
      growth: data.summary?.globalGrowthPercentage || 0,
    },

    // Métricas de tiendas
    storeMetrics: {
      total: data.summary?.totalStores || 0,
      avgGrowth: data.summary?.globalGrowthPercentage || 0,
      topStore: "Tienda Principal",
      worstStore: "En Análisis",
    },

    // Métricas de usuarios
    userMetrics: {
      total: data.summary?.totalUsers || 0,
      avgGrowth: data.summary?.globalGrowthPercentage || 0,
      topUser: "Operador Principal",
      worstUser: "En Entrenamiento",
    },

    // Métricas POS
    posMetrics: {
      totalOperators: data.summary?.totalPOS || 0,
      avgEfficiency: 8.5,
      topOperator: "POS Principal",
      worstOperator: "En Mejora",
    },

    // Estado de queries
    isLoading: dashboardQuery.isLoading,
    isError: dashboardQuery.isError,
    lastUpdated: dashboardQuery.dataUpdatedAt,
  };
};
